import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Truck, 
  FileText, 
  AlertTriangle, 
  CheckCircle, 
  IndianRupee,
  RefreshCw,
  ArrowRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getDashboardSummary } from '../services/api';
import { DashboardSummary, Alert } from '../types';
import clsx from 'clsx';

export default function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getDashboardSummary();
      if (response.success && response.data) {
        setSummary(response.data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <p className="text-danger-600 mb-4">{error}</p>
        <button onClick={fetchData} className="btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!summary) return null;

  const statsCards = [
    { 
      label: 'Total Vehicles', 
      value: summary.totalVehicles, 
      icon: Truck, 
      color: 'primary',
      link: '/vehicles'
    },
    { 
      label: 'Pending Challans', 
      value: summary.pendingChallans, 
      icon: FileText, 
      color: 'warning',
      link: '/challans?status=PENDING'
    },
    { 
      label: 'Amount Pending', 
      value: `₹${summary.totalAmountPending.toLocaleString()}`, 
      icon: IndianRupee, 
      color: 'danger',
      link: '/challans?status=PENDING'
    },
    { 
      label: 'Clean Vehicles', 
      value: `${summary.cleanVehicles}`,
      subValue: `${Math.round((summary.cleanVehicles / summary.totalVehicles) * 100) || 0}%`,
      icon: CheckCircle, 
      color: 'success',
      link: '/vehicles?status=clean'
    },
  ];

  const colorMap: Record<string, string> = {
    primary: 'bg-primary-50 text-primary-600',
    warning: 'bg-warning-50 text-warning-600',
    danger: 'bg-danger-50 text-danger-600',
    success: 'bg-success-50 text-success-600',
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statsCards.map((stat) => (
          <Link key={stat.label} to={stat.link} className="card p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.label}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                {stat.subValue && (
                  <p className="text-sm text-gray-500 mt-1">{stat.subValue}</p>
                )}
              </div>
              <div className={clsx('p-2.5 rounded-lg', colorMap[stat.color])}>
                <stat.icon className="w-5 h-5" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Charts and Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Challans by State Chart */}
        <div className="lg:col-span-2 card p-5">
          <h3 className="text-sm font-medium text-gray-900 mb-4">Challans by State</h3>
          {summary.challansByState.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary.challansByState}>
                  <XAxis dataKey="state" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              No challan data available
            </div>
          )}
        </div>

        {/* Recent Alerts */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-900">Recent Alerts</h3>
            <Link to="/alerts" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {summary.recentAlerts.length > 0 ? (
            <div className="space-y-3">
              {summary.recentAlerts.map((alert) => (
                <AlertItem key={alert.alertId} alert={alert} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-10 h-10 mx-auto mb-2 text-success-500" />
              <p>No active alerts</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Challans</p>
              <p className="text-xl font-semibold text-gray-900 mt-1">{summary.totalChallans}</p>
            </div>
            <div className="text-sm text-gray-500">
              <span className="text-success-600">{summary.paidChallans} paid</span>
              {' / '}
              <span className="text-warning-600">{summary.pendingChallans} pending</span>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Amount Paid</p>
              <p className="text-xl font-semibold text-success-600 mt-1">
                ₹{summary.totalAmountPaid.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Blocked Vehicles</p>
              <p className="text-xl font-semibold text-danger-600 mt-1">{summary.blockedVehicles}</p>
            </div>
            <Link to="/vehicles?status=blocked" className="text-sm text-primary-600 hover:underline">
              View →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlertItem({ alert }: { alert: Alert }) {
  const severityStyles = {
    info: 'border-l-primary-500 bg-primary-50',
    warning: 'border-l-warning-500 bg-warning-50',
    critical: 'border-l-danger-500 bg-danger-50',
  };

  const severityIcons = {
    info: <FileText className="w-4 h-4 text-primary-600" />,
    warning: <AlertTriangle className="w-4 h-4 text-warning-600" />,
    critical: <AlertTriangle className="w-4 h-4 text-danger-600" />,
  };

  return (
    <div className={clsx(
      'p-3 rounded-lg border-l-4',
      severityStyles[alert.severity]
    )}>
      <div className="flex items-start gap-2">
        {severityIcons[alert.severity]}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{alert.title}</p>
          <p className="text-xs text-gray-600 truncate">{alert.vehicleNumber}</p>
        </div>
      </div>
    </div>
  );
}
